import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Role-gated route wrapper.
 *
 * Accepts a list of allowed short-code roles (e.g. ['JADM', 'CADM']). The user
 * profile may carry either the long form ('jobasti_admin') or short form
 * ('JADM') depending on origin, so we normalize both before comparing.
 */
const ROLE_TO_SHORT = {
  jobasti_admin: 'JADM',
  jobasti_recruiter: 'JREC',
  client_admin: 'CADM',
  client_recruiter: 'CREC',
  prospect: 'PROS',
  sales_executive: 'SEXE',
};

const SHORT_CODES = new Set(['JADM', 'JREC', 'CADM', 'CREC', 'PROS', 'RADM', 'RECR', 'SEXE']);

const ProtectedRoute = ({ children, roles = [] }) => {
  const { isAuthenticated, userProfile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (roles.length > 0) {
    const rawRole = userProfile?.role || '';
    // Already a short code? use it; otherwise translate from the long form.
    const userRole = SHORT_CODES.has(rawRole) ? rawRole : ROLE_TO_SHORT[rawRole] || null;

    // Bidirectional aliases — different parts of the codebase use either
    // RADM/RECR or CADM/CREC for "client admin" / "client recruiter".
    // Treat them as synonyms in both directions so an allow-list containing
    // either form admits both.
    const expanded = new Set(roles);
    if (expanded.has('CADM')) expanded.add('RADM');
    if (expanded.has('RADM')) expanded.add('CADM');
    if (expanded.has('CREC')) expanded.add('RECR');
    if (expanded.has('RECR')) expanded.add('CREC');
    // Also auto-add long form for forward compatibility
    if (expanded.has('JADM')) expanded.add('jobasti_admin');
    if (expanded.has('CADM') || expanded.has('RADM')) expanded.add('client_admin');
    if (expanded.has('CREC') || expanded.has('RECR')) expanded.add('client_recruiter');
    if (expanded.has('PROS')) expanded.add('prospect');
    if (expanded.has('SEXE')) expanded.add('sales_executive');

    // Match against both the normalized short code AND the raw value.
    const userMatch = (userRole && expanded.has(userRole)) || (rawRole && expanded.has(rawRole));
    if (!userMatch) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
